import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * In the browser the API is same-origin and a relative path is correct. The
 * prerenderer has no origin to be relative to, so it sets an absolute base
 * before rendering.
 */
let baseUrlOverride: string | undefined;
let localeOverride: string | undefined;

export function setApiBaseUrl(url: string): void {
  baseUrlOverride = url;
}

/**
 * Sets the language sent on `Accept-Language`.
 *
 * The browser takes it from the document, but there is no document while
 * prerendering — without this the Spanish pages would be built from English
 * data.
 */
export function setApiLocale(locale: string): void {
  localeOverride = locale;
}

function currentBaseUrl(): string {
  return baseUrlOverride ?? import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
}

function currentLocale(): string | undefined {
  if (localeOverride) return localeOverride;
  return typeof document !== 'undefined' ? document.documentElement.lang : undefined;
}

/**
 * Single entry point to the backend. Every feature injects its endpoints here so
 * there is one cache, one auth header and one base URL.
 *
 * The frontend holds no business logic: prices, availability and validation are
 * always whatever the backend returned.
 */
export const baseApi = createApi({
  reducerPath: 'api',
  // Resolved per request rather than at module load, so the same slice works
  // unchanged in the browser and under the prerenderer.
  baseQuery: (async (args, api, extraOptions) => {
    const result = await fetchBaseQuery({
      baseUrl: currentBaseUrl(),
      prepareHeaders: (headers, { getState }) => {
        const token = (getState() as { auth?: { session?: { accessToken?: string } } })
          .auth?.session?.accessToken;
        if (token) headers.set('Authorization', `Bearer ${token}`);

        // A query that already set its own Accept-Language (locale-sensitive
        // content endpoints pass the locale explicitly as part of their args —
        // see contentApi.ts) wins: the locale context updates synchronously on
        // navigation, while document.documentElement.lang is only set from
        // RootLayout's effect, which can still be mid-flight on the very first
        // request after a locale switch.
        if (!headers.has('Accept-Language')) {
          const locale = currentLocale();
          if (locale) headers.set('Accept-Language', locale);
        }

        return headers;
      },
    })(args, api, extraOptions);

    // An expired or revoked token should return the operator to the sign-in
    // screen rather than leaving every panel showing an error.
    if (result.error?.status === 401) {
      api.dispatch({ type: "auth/signedOut" });
    }

    return result;
  }) as BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  tagTypes: [
    'Location',
    'Trip',
    'ShuttleType',
    'ExtraService',
    'Car',
    'CompanyService',
    'Reservation',
    'Availability',
    'Content',
    'Review',
    'AdminUser',
    'Settings',
    'Coupon',
    'AboutUs',
    'CustomerReview',
    'Dashboard',
  ],
  endpoints: () => ({}),
});
